import { getCsrfParams } from '~/common/helpers/csrf'

const SPINNER_THUMBNAIL =
  '<div class="js-embed-loading relative group/thumbnail js-thumbnail flex-none border h-full rounded-lg border-border-base aspect-square">' +
  '<div class="thumbnail h-full overflow-hidden rounded-lg justify-center flex flex-wrap content-center bg-bg-alt-base">' +
  '<span class="w-10 h-10 flex rounded-full bg-black/20 text-neutral-400"><i class="fa fa-spinner fa-spin fa-lg m-auto"></i></span>' +
  '</div></div>'

// Track URL → embed_id per form. Values: 'pending' while fetching, embed_id on success.
// When checking duplicates, we verify the embed_id is still in the input — if the user
// removed the attachment, the URL can be re-added.
const embedUrls = new WeakMap()

function getUrlMap(form) {
  if (!embedUrls.has(form)) embedUrls.set(form, new Map())
  return embedUrls.get(form)
}

// Whether `sourceUrl` is already on `form`: 'pending' while its fetch is in flight,
// 'attached' while its thumbnail is still in the attachments area, otherwise null.
export function embedAttachState(sourceUrl, form) {
  const existingId = embedUrls.get(form)?.get(sourceUrl)
  if (!existingId) return null
  if (existingId === 'pending') return 'pending'

  const previewContainer = form.querySelector('.js-splide-container')
  const thumbnail = previewContainer?.querySelector(
    `#js-preview-thumbnail-embed-${existingId}`
  )
  return thumbnail ? 'attached' : null
}

// Create a video embed via POST /embeds and wire it into the form's attachment system.
// `form` must contain a `.js-splide-container` and an `input[id$="embed_ids_"]`.
// Shows a spinner placeholder in the attachments area while loading.
export function createEmbed(sourceUrl, objectName, form) {
  const embedInput = form.querySelector('input[id$="embed_ids_"]')
  const previewContainer = form.querySelector('.js-splide-container')
  if (!embedInput || !previewContainer)
    return Promise.reject(new Error('Missing embed form elements'))

  const attachState = embedAttachState(sourceUrl, form)
  if (attachState === 'attached')
    window.showNotice('alert', 'Already attached this video')
  if (attachState) return Promise.resolve(null)

  // Any remaining entry is an embed the user removed, so the URL can be re-added.
  const urlMap = getUrlMap(form)
  urlMap.set(sourceUrl, 'pending')

  // Optimistic UI: show spinner placeholder immediately
  previewContainer.dispatchEvent(
    new CustomEvent('splide:addItem', {
      detail: { thumbnail: SPINNER_THUMBNAIL, objectName: 'embed' },
    })
  )

  const csrf = getCsrfParams()
  const body = { source_url: sourceUrl, object_name: objectName }
  body[csrf[0]] = csrf[1]

  return fetch('/embeds', {
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  })
    .then((response) =>
      response.status === 200
        ? response.json()
        : response.json().then((json) => {
            throw new Error(json.error)
          })
    )
    .then((data) => {
      removeLoadingPlaceholder(previewContainer)
      urlMap.set(sourceUrl, data.id)

      if (embedInput.value) {
        embedInput.value += `,${data.id}`
      } else {
        embedInput.value = `${data.id}`
      }
      previewContainer.dispatchEvent(
        new CustomEvent('splide:addItem', {
          detail: {
            thumbnail: data.thumbnail_preview,
            preview: data.preview,
            objectId: data.id,
            objectName: 'embed',
          },
        })
      )
      form.dispatchEvent(new CustomEvent('embeds-changed', { bubbles: true }))
      return data
    })
    .catch((error) => {
      urlMap.delete(sourceUrl)
      removeLoadingPlaceholder(previewContainer)
      throw error
    })
}

function removeLoadingPlaceholder(previewContainer) {
  const placeholder = previewContainer.querySelector('.js-embed-loading')
  if (placeholder) {
    const slide = placeholder.closest('.splide__slide')
    if (slide) slide.remove()
  }
}
